/**
 * The FAQ's questions: groups, questions, their order, both languages.
 *
 * Saved as one tree. The screen holds the whole FAQ and sends it back in one
 * save; the backend updates what kept its id, creates what has none and
 * deletes what is no longer there. That is what makes drag-to-reorder and
 * "move to another group" simple — but it also means a screen left open while
 * someone else edits the FAQ would, on save, delete what they added. So the
 * load comes with a `version`, the save sends it back, and a save against a
 * FAQ that has changed since is refused. The admin is then offered a reload
 * rather than a silent overwrite.
 *
 * Reordering uses native drag events like the homepage arrange screen.
 * Moving a question between groups is a menu, not a drag: dragging across
 * long, expanded groups is hard to aim.
 *
 * Only an opened question renders its rich-text editors. Twenty-odd pairs of
 * Quill editors open at once would make the page crawl.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDown, ChevronRight, Eye, EyeOff, GripVertical, Plus, RotateCcw, Save, Trash2,
} from "lucide-react";
import swal from "sweetalert";
import axiosInstance from "../../../redux/api";
import RichTextField from "./RichTextField";
import { errorText } from "./shared";

let nextLocalKey = 1;
const localKey = (prefix) => `${prefix}-new-${nextLocalKey++}`;

// The API's tree → the screen's. Each row gets a stable React key: its id,
// or a local one for rows not saved yet.
const fromApi = (groups) =>
  (groups || []).map((group) => ({
    key: `g-${group.id}`,
    id: group.id,
    title: group.title || "",
    ar_title: group.ar_title || "",
    status: group.status_label || "published",
    items: (group.items || []).map((item) => ({
      key: `i-${item.id}`,
      id: item.id,
      question: item.question || "",
      ar_question: item.ar_question || "",
      answer_html: item.answer_html || "",
      ar_answer_html: item.ar_answer_html || "",
      status: item.status_label || "published",
    })),
  }));

// The screen's tree → the save body. Local keys never leave the browser.
const toApi = (groups) =>
  groups.map((group) => ({
    ...(group.id ? { id: group.id } : {}),
    title: group.title.trim(),
    ar_title: group.ar_title.trim() || null,
    status: group.status,
    items: group.items.map((item) => ({
      ...(item.id ? { id: item.id } : {}),
      question: item.question.trim(),
      ar_question: item.ar_question.trim() || null,
      answer_html: item.answer_html,
      ar_answer_html: item.ar_answer_html || null,
      status: item.status,
    })),
  }));

// "<p><br></p>" is what an emptied editor leaves behind.
const isBlankHtml = (html) =>
  !(html || "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();

/** Everything the admin must fix before a save is worth sending. */
const problems = (groups) => {
  const found = [];
  groups.forEach((group, g) => {
    const name = group.title.trim() || `Group ${g + 1}`;
    if (!group.title.trim()) found.push(`Group ${g + 1} needs an English title.`);
    group.items.forEach((item, i) => {
      if (!item.question.trim()) found.push(`${name}, question ${i + 1}: the English question is empty.`);
      if (isBlankHtml(item.answer_html)) found.push(`${name}, question ${i + 1}: the English answer is empty.`);
    });
  });
  return found;
};

const move = (list, from, to) => {
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

const FaqTreeEditor = ({ editable, onDirtyChange, onSaved }) => {
  const [groups, setGroups] = useState([]);
  const [snapshot, setSnapshot] = useState("[]"); // the tree as last loaded, to tell if it changed
  const [version, setVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);
  const [open, setOpen] = useState({}); // which groups/questions are expanded, by key

  // What is being dragged: {kind: "group", index} or {kind: "item", group, index}.
  // A ref, not state — it changes on every dragover.
  const dragging = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get("admin/pages/faq");
      const tree = fromApi(data?.response?.groups);
      setGroups(tree);
      setSnapshot(JSON.stringify(toApi(tree)));
      setVersion(data?.response?.version || null);
      setErrors([]);
    } catch (error) {
      swal({ text: errorText(error, "Could not load the FAQ"), icon: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const dirty = useMemo(() => JSON.stringify(toApi(groups)) !== snapshot, [groups, snapshot]);

  useEffect(() => {
    if (onDirtyChange) onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  // ---- editing ---------------------------------------------------------

  const updateGroup = (g, changes) =>
    setGroups((current) => current.map((group, index) => (index === g ? { ...group, ...changes } : group)));

  const updateItem = (g, i, changes) =>
    setGroups((current) =>
      current.map((group, gi) =>
        gi !== g ? group : {
          ...group,
          items: group.items.map((item, ii) => (ii === i ? { ...item, ...changes } : item)),
        }));

  const addGroup = () => {
    const key = localKey("g");
    setGroups((current) => [
      ...current,
      { key, id: null, title: "", ar_title: "", status: "published", items: [] },
    ]);
    setOpen((current) => ({ ...current, [key]: true }));
  };

  const addItem = (g) => {
    const key = localKey("i");
    setGroups((current) =>
      current.map((group, gi) =>
        gi !== g ? group : {
          ...group,
          items: [...group.items, {
            key, id: null, question: "", ar_question: "", answer_html: "", ar_answer_html: "", status: "published",
          }],
        }));
    setOpen((current) => ({ ...current, [groups[g].key]: true, [key]: true }));
  };

  const removeGroup = async (g) => {
    const group = groups[g];
    const count = group.items.length;
    const ok = await swal({
      title: "Delete this group?",
      text: count
        ? `"${group.title || "Untitled group"}" and its ${count} question${count === 1 ? "" : "s"} will be removed when you save.`
        : `"${group.title || "Untitled group"}" will be removed when you save.`,
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    });
    if (ok) setGroups((current) => current.filter((_, index) => index !== g));
  };

  const removeItem = async (g, i) => {
    const item = groups[g].items[i];
    const ok = await swal({
      title: "Delete this question?",
      text: `"${item.question || "Untitled question"}" will be removed when you save.`,
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    });
    if (ok) {
      setGroups((current) =>
        current.map((group, gi) => (gi !== g ? group : { ...group, items: group.items.filter((_, ii) => ii !== i) })));
    }
  };

  const moveItemToGroup = (g, i, target) => {
    setGroups((current) => {
      const item = current[g].items[i];
      return current.map((group, gi) => {
        if (gi === g) return { ...group, items: group.items.filter((_, ii) => ii !== i) };
        if (gi === target) return { ...group, items: [...group.items, item] };
        return group;
      });
    });
    setOpen((current) => ({ ...current, [groups[target].key]: true }));
  };

  // ---- drag to reorder -------------------------------------------------

  const onGroupDragEnter = (g) => {
    const from = dragging.current;
    if (!from || from.kind !== "group" || from.index === g) return;
    setGroups((current) => move(current, from.index, g));
    dragging.current = { kind: "group", index: g };
  };

  const onItemDragEnter = (g, i) => {
    const from = dragging.current;
    // Only within one group; moving between groups is the menu.
    if (!from || from.kind !== "item" || from.group !== g || from.index === i) return;
    setGroups((current) =>
      current.map((group, gi) => (gi !== g ? group : { ...group, items: move(group.items, from.index, i) })));
    dragging.current = { kind: "item", group: g, index: i };
  };

  const endDrag = () => {
    dragging.current = null;
  };

  // ---- save ------------------------------------------------------------

  const handleSave = async () => {
    const found = problems(groups);
    if (found.length) {
      setErrors(found);
      return;
    }
    setSaving(true);
    setErrors([]);
    try {
      const { data } = await axiosInstance.put("admin/pages/faq", { version, groups: toApi(groups) });
      const tree = fromApi(data?.response?.groups);
      setGroups(tree);
      setSnapshot(JSON.stringify(toApi(tree)));
      setVersion(data?.response?.version || null);
      if (onSaved) onSaved(data?.response);
      swal({ text: "FAQ saved", icon: "success", timer: 1400, buttons: false });
    } catch (error) {
      const message = errorText(error, "Could not save the FAQ");
      if (error?.response?.status === 422) {
        // Someone else saved the FAQ after this screen loaded it.
        const reload = await swal({
          title: "The FAQ changed while you were editing",
          text: `${message}\n\nReloading shows their changes and discards yours on this screen. Keep editing if you want to copy your text first.`,
          icon: "warning",
          buttons: ["Keep editing", "Reload"],
        });
        if (reload) load();
      } else {
        setErrors(message.split("\n"));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = async () => {
    const ok = await swal({
      text: "Discard your changes to the questions?",
      icon: "warning",
      buttons: ["Keep them", "Discard"],
      dangerMode: true,
    });
    if (ok) load();
  };

  // ---- render ----------------------------------------------------------

  if (loading) {
    return <div className="ds-empty-state" style={{ height: 160 }}>Loading questions...</div>;
  }

  const questionCount = groups.reduce((total, group) => total + group.items.length, 0);
  const locked = !editable || saving;

  return (
    <div className="ds-form-container cp-faq">
      <div className="cp-faq__head">
        <div>
          <h5 style={{ margin: 0 }}>Questions</h5>
          <span className="ds-hint">
            {groups.length} group{groups.length === 1 ? "" : "s"} · {questionCount} question{questionCount === 1 ? "" : "s"}
            {editable && " · drag ⋮⋮ to reorder"}
          </span>
        </div>
        {editable && (
          <div className="cp-faq__actions">
            {dirty && <span className="cp-dirty-note">Unsaved changes</span>}
            {dirty && (
              <button type="button" className="ds-btn-secondary" onClick={handleDiscard} disabled={saving}>
                <RotateCcw size={14} style={{ marginInlineEnd: 4 }} />
                Discard
              </button>
            )}
            <button type="button" className="ds-btn-primary" onClick={handleSave} disabled={saving || !dirty}>
              <Save size={15} style={{ marginInlineEnd: 6 }} />
              {saving ? "Saving..." : dirty ? "Save questions" : "Saved"}
            </button>
          </div>
        )}
      </div>

      {errors.length > 0 && <div className="cp-errors">{errors.join("\n")}</div>}

      {groups.length === 0 && (
        <div className="ds-empty-state" style={{ height: 120 }}>No questions yet</div>
      )}

      {groups.map((group, g) => {
        const groupOpen = Boolean(open[group.key]);
        const hidden = group.status !== "published";
        return (
          <div
            key={group.key}
            className={`cp-faq__group${hidden ? " cp-faq__group--hidden" : ""}`}
            onDragEnter={() => onGroupDragEnter(g)}
            onDragOver={(e) => e.preventDefault()}
          >
            <div className="cp-faq__group-bar">
              {editable && (
                <span
                  className="cp-faq__grip"
                  draggable
                  title="Drag to reorder groups"
                  onDragStart={() => { dragging.current = { kind: "group", index: g }; }}
                  onDragEnd={endDrag}
                >
                  <GripVertical size={16} />
                </span>
              )}
              <button
                type="button"
                className="cp-faq__toggle"
                aria-expanded={groupOpen}
                onClick={() => setOpen((current) => ({ ...current, [group.key]: !groupOpen }))}
              >
                {groupOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                <span className="cp-faq__group-title">{group.title || "Untitled group"}</span>
                <span className="cp-faq__count">{group.items.length}</span>
                {hidden && <span className="ds-badge ds-badge--inactive">Hidden</span>}
              </button>
              {editable && (
                <div className="cp-faq__row-actions">
                  <button
                    type="button"
                    className="ds-btn-secondary"
                    disabled={locked}
                    title={hidden ? "Show this group on the site" : "Hide this group from the site"}
                    onClick={() => updateGroup(g, { status: hidden ? "published" : "draft" })}
                  >
                    {hidden ? <Eye size={14} /> : <EyeOff size={14} />}
                  </button>
                  <button type="button" className="ds-btn-delete" disabled={locked} title="Delete group" onClick={() => removeGroup(g)}>
                    <Trash2 size={14} />
                  </button>
                </div>
              )}
            </div>

            {groupOpen && (
              <div className="cp-faq__group-body">
                <div className="cp-lang-grid">
                  <div className="ds-form-group">
                    <label htmlFor={`${group.key}-title`}>Group title (English) *</label>
                    <input
                      id={`${group.key}-title`}
                      type="text"
                      value={group.title}
                      disabled={locked}
                      maxLength={255}
                      onChange={(e) => updateGroup(g, { title: e.target.value })}
                    />
                  </div>
                  <div className="ds-form-group">
                    <label htmlFor={`${group.key}-ar_title`}>Group title (Arabic)</label>
                    <input
                      id={`${group.key}-ar_title`}
                      type="text"
                      dir="rtl"
                      value={group.ar_title}
                      disabled={locked}
                      maxLength={255}
                      onChange={(e) => updateGroup(g, { ar_title: e.target.value })}
                    />
                  </div>
                </div>

                {group.items.map((item, i) => {
                  const itemOpen = Boolean(open[item.key]);
                  const itemHidden = item.status !== "published";
                  return (
                    <div
                      key={item.key}
                      className={`cp-faq__item${itemHidden ? " cp-faq__item--hidden" : ""}`}
                      onDragEnter={(e) => {
                        // Only question drags stop here; a group being dragged
                        // must still reach its group's handler to reorder.
                        if (dragging.current?.kind !== "item") return;
                        e.stopPropagation();
                        onItemDragEnter(g, i);
                      }}
                      onDragOver={(e) => e.preventDefault()}
                    >
                      <div className="cp-faq__item-bar">
                        {editable && (
                          <span
                            className="cp-faq__grip"
                            draggable
                            title="Drag to reorder questions"
                            onDragStart={(e) => { e.stopPropagation(); dragging.current = { kind: "item", group: g, index: i }; }}
                            onDragEnd={endDrag}
                          >
                            <GripVertical size={15} />
                          </span>
                        )}
                        <button
                          type="button"
                          className="cp-faq__toggle"
                          aria-expanded={itemOpen}
                          onClick={() => setOpen((current) => ({ ...current, [item.key]: !itemOpen }))}
                        >
                          {itemOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                          <span className="cp-faq__question">{item.question || "Untitled question"}</span>
                          {itemHidden && <span className="ds-badge ds-badge--inactive">Hidden</span>}
                        </button>
                        {editable && (
                          <div className="cp-faq__row-actions">
                            {groups.length > 1 && (
                              <select
                                aria-label="Move to group"
                                value=""
                                disabled={locked}
                                onChange={(e) => moveItemToGroup(g, i, Number(e.target.value))}
                              >
                                <option value="" disabled>Move to…</option>
                                {groups.map((target, t) => (t === g ? null : (
                                  <option key={target.key} value={t}>{target.title || `Group ${t + 1}`}</option>
                                )))}
                              </select>
                            )}
                            <button
                              type="button"
                              className="ds-btn-secondary"
                              disabled={locked}
                              title={itemHidden ? "Show this question on the site" : "Hide this question from the site"}
                              onClick={() => updateItem(g, i, { status: itemHidden ? "published" : "draft" })}
                            >
                              {itemHidden ? <Eye size={14} /> : <EyeOff size={14} />}
                            </button>
                            <button type="button" className="ds-btn-delete" disabled={locked} title="Delete question" onClick={() => removeItem(g, i)}>
                              <Trash2 size={14} />
                            </button>
                          </div>
                        )}
                      </div>

                      {itemOpen && (
                        <div className="cp-faq__item-body">
                          <div className="cp-lang-grid">
                            <div className="ds-form-group">
                              <label htmlFor={`${item.key}-q`}>Question (English) *</label>
                              <input
                                id={`${item.key}-q`}
                                type="text"
                                value={item.question}
                                disabled={locked}
                                maxLength={500}
                                onChange={(e) => updateItem(g, i, { question: e.target.value })}
                              />
                            </div>
                            <div className="ds-form-group">
                              <label htmlFor={`${item.key}-arq`}>Question (Arabic)</label>
                              <input
                                id={`${item.key}-arq`}
                                type="text"
                                dir="rtl"
                                value={item.ar_question}
                                disabled={locked}
                                maxLength={500}
                                onChange={(e) => updateItem(g, i, { ar_question: e.target.value })}
                              />
                            </div>
                            <div className="ds-form-group">
                              <label>Answer (English) *</label>
                              <RichTextField
                                minHeight={120}
                                readOnly={locked}
                                value={item.answer_html}
                                onChange={(html) => updateItem(g, i, { answer_html: html })}
                              />
                            </div>
                            <div className="ds-form-group">
                              <label>Answer (Arabic)</label>
                              <RichTextField
                                rtl
                                minHeight={120}
                                readOnly={locked}
                                value={item.ar_answer_html}
                                onChange={(html) => updateItem(g, i, { ar_answer_html: html })}
                                placeholder="يُعرض الجواب الإنجليزي إذا تُرك فارغًا"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {editable && (
                  <button type="button" className="ds-btn-secondary cp-faq__add" disabled={locked} onClick={() => addItem(g)}>
                    <Plus size={14} style={{ marginInlineEnd: 4 }} />
                    Add question
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}

      {editable && (
        <button type="button" className="ds-btn-secondary cp-faq__add" disabled={locked} onClick={addGroup}>
          <Plus size={14} style={{ marginInlineEnd: 4 }} />
          Add group
        </button>
      )}
    </div>
  );
};

export default FaqTreeEditor;
